import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { PermissaoEnum } from '@shared/enums/usuario/permissao.enum';
import { AuthService } from '@shared/services/apis/usuario/auth.service';
import { LookupItemStruct } from '@shared/services/structs/pessoa/lookup-item.struct';

import { environment } from '../../../../../../environments/environment';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { TipoContatoEnum } from '../../../../../shared/enums/pessoa/tipo-contato.enum';
import { SituacaoVendedorEnum } from '@shared/enums/pessoa/situacao-vendedor.enum';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { PessoaService } from '@shared/services/apis/pessoa/pessoa.service';
import { Masks, MaskService } from '@shared/services/mask.service';
import { OportunidadeService } from '../../../../../shared/services/apis/vendas/oportunidade.service';
import { ContatoPessoaStruct } from '../../../../../shared/services/structs/pessoa/contato-pessoa.struct';
import { VendedorLookupStruct } from '@shared/services/structs/pessoa/vendedor-lookup.struct';
import { VendaEscopoStruct } from '@shared/services/structs/vendas/venda-escopo.struct';
import {
  LookupOportunidade,
  OportunidadeKanbanItem,
  TagOportunidade,
} from '../../../../../shared/services/models/vendas/oportunidade.model';
import { CriarOportunidadeRequest } from '../../../../../shared/services/requests/vendas/oportunidade.request';
import {
  ID_POR_PRIORIDADE,
  OportunidadeStage,
  PrioridadeChave,
  PRIORIDADES,
} from '../../crm-oportunidades.models';

export interface NovaOportunidadeData {
  /** Etapas do fluxo, na ordem configurada. A criação só oferece as não-finais. */
  stages: OportunidadeStage[];
  origens: LookupOportunidade[];
  canais: LookupOportunidade[];
  tags: TagOportunidade[];
  /** Todos os vendedores da empresa; o campo oferece só os ativos dentro do escopo. */
  vendedores: VendedorLookupStruct[];
  /** Escopo do usuário logado: vendedor logado já nasce como responsável. */
  escopo: VendaEscopoStruct;
  /** Vendedores que o usuário enxerga; vazia = sem restrição. */
  idsVendedoresVisiveis: number[];
  /** "Restringir acesso dos vendedores" do cadastro de Pessoa. */
  isRestritoVendedor: boolean;
  /** Abertura vinda do cadastro de Pessoa (IA-01): cliente já escolhido. */
  idPessoaClienteInicial?: number;
  nomeClienteInicial?: string;
  /** Vendedor ativo do cadastro da pessoa inicial; só vale para quem não é vendedor (RC-04). */
  idVendedorInicial?: number | null;
  /** IA-04: aviso de origem mostrado no topo do formulário. */
  textoOrigem?: string | null;
}

export interface NovaOportunidadeResult {
  oportunidade: OportunidadeKanbanItem;
}

/**
 * Cadastro de nova oportunidade (US §4 — CA12, CA13).
 *
 * Cliente e contato usam busca no servidor (`listPessoaSelect`) em vez de lista carregada:
 * o endpoint pagina em 20, então uma lista local mostraria só as 20 primeiras pessoas da
 * empresa.
 */
@Component({
  selector: 'app-nova-oportunidade-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    NgxMaskDirective,
    SelectPesquisavelComponent,
    AlertCardComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './nova-oportunidade-dialog.component.html',
  styleUrl: './nova-oportunidade-dialog.component.css',
})
export class NovaOportunidadeDialogComponent {
  readonly prioridades = PRIORIDADES;

  /** Busca no servidor: o select-pesquisavel manda `textoBusca` e lê `{ list }`. */
  readonly urlPessoa = `${environment.urlApiPessoa}Lookup/listPessoaSelect`;

  /**
   * Só pessoas marcadas como Cliente em pessoa_tipo_contato entram no seletor, e com
   * "Restringir acesso dos vendedores" o vendedor logado vê os dele e os sem vendedor (RC-01).
   */
  readonly filtroCliente = { idTipoContato: TipoContatoEnum.Cliente, restringirVendedor: true };

  /** RC-03: a lista de clientes está recortada pelo vendedor logado. */
  readonly hintCliente: string;

  /** Vendedores que podem ser atribuídos: ativos e dentro do escopo de quem cria (RV-02). */
  readonly opcoesVendedor: VendedorLookupStruct[];

  /** RC-04: o cliente escolhido não tem vendedor ativo para preencher o campo. */
  hintVendedor = '';

  /** Contatos do cliente escolhido (pessoa_contato). Vazio até haver cliente. */
  contatos: ContatoPessoaStruct[] = [];
  carregandoContatos = false;

  /** Telefone com a mesma máscara do cadastro de Pessoa. */
  readonly masks: Masks = MaskService.getMasks();

  /** Criar contato exige Alterar do CRM e Alterar Pessoas; null quando o usuário tem as duas. */
  private readonly motivoContatoSemPermissao: string | null;

  /**
   * Contato fora da lista ("+ Novo contato"): a linha do contato vira nome, telefone e e-mail, e o
   * contato é gravado no cadastro do cliente ao criar a oportunidade.
   */
  contatoNovoAberto = false;
  readonly contatoNovoForm = new FormGroup({
    nome: new FormControl('', { nonNullable: true, validators: Validators.required }),
    telefone: new FormControl('', { nonNullable: true, validators: Validators.required }),
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
  });

  /** Cliente inicial como opção já carregada: sem isto o campo de busca no servidor mostraria só o id. */
  readonly clientesIniciais: LookupItemStruct[];

  /** IA-03: o cliente escolhido já tem oportunidade em andamento. Só avisa, não bloqueia. */
  clienteComOportunidadeEmAndamento = false;

  /** Etapa final é destino de encerramento, não de criação. */
  readonly etapasDisponiveis: OportunidadeStage[];

  titulo = '';
  clienteControl = new FormControl<number | null>(null, Validators.required);
  contatoControl = new FormControl<number | null>(null);
  vendedorControl = new FormControl<number | null>(null, Validators.required);

  idEtapaKanban: number | null = null;
  idOrigemLead: number | null = null;
  idCanalContato: number | null = null;
  prioridade: PrioridadeChave = 'media';
  valorEstimado: number | null = null;
  /** Tags escolhidas: o select múltiplo mostra as escolhidas como selos logo abaixo. */
  tagsControl = new FormControl<number[]>([], { nonNullable: true });

  salvando = false;

  constructor(
    public dialogRef: MatDialogRef<NovaOportunidadeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NovaOportunidadeData,
    private oportunidadeService: OportunidadeService,
    private pessoaLookupService: PessoaLookupService,
    private pessoaService: PessoaService,
    private alertService: AlertService,
    private authService: AuthService
  ) {
    const listaPermissoes = this.authService.getUserInfos()?.listaPermissoes;
    this.motivoContatoSemPermissao = !listaPermissoes?.includes(PermissaoEnum.Vendas_Crm_Alterar)
      ? 'Sem permissão para alterar o CRM.'
      : !listaPermissoes.includes(PermissaoEnum.Cadastros_Pessoas_Alterar)
        ? 'Sem permissão para alterar o cadastro de pessoas.'
        : null;

    this.etapasDisponiveis = (data.stages ?? []).filter(s => !s.isFinal && !s.isEspecial);

    // Nasce na primeira etapa do fluxo, como o board faz quando a etapa não é informada.
    this.idEtapaKanban = this.etapasDisponiveis[0]?.idEtapaKanban ?? null;

    const visiveis = data.idsVendedoresVisiveis ?? [];
    this.opcoesVendedor = (data.vendedores ?? []).filter(v => v.idSituacaoVendedor !== SituacaoVendedorEnum.Inativo
      && (!visiveis.length || visiveis.includes(v.idVendedor)));

    this.hintCliente = data.isRestritoVendedor && data.escopo.isVendedor
      ? 'A lista mostra os clientes vinculados a você e os sem vendedor.'
      : '';

    // RV-01: o vendedor logado é o responsável. RC-04: para quem não é vendedor, o vendedor ativo do
    // cliente inicial; senão vazio para escolher.
    const idVendedorInicial = data.escopo.isVendedor ? data.escopo.idVendedor : data.idVendedorInicial;
    this.vendedorControl.setValue(this.vendedorDisponivel(idVendedorInicial));
    if (data.idPessoaClienteInicial && !data.escopo.isVendedor && this.vendedorControl.value == null) {
      this.hintVendedor = 'Este cliente não tem vendedor ativo. Escolha o vendedor responsável.';
    }

    this.clientesIniciais = data.idPessoaClienteInicial
      ? [{ id: data.idPessoaClienteInicial, descricao: data.nomeClienteInicial ?? '', codigo: '' }]
      : [];

    // Contato pertence ao cliente: trocar de cliente invalida o contato escolhido e o contato rápido.
    this.clienteControl.valueChanges.subscribe(idPessoa => {
      this.contatoControl.setValue(null);
      this.contatos = [];
      this.contatoNovoAberto = false;
      this.clienteComOportunidadeEmAndamento = false;
      if (idPessoa) {
        this.carregarContatos(idPessoa);
        this.verificarOportunidadeEmAndamento(idPessoa);
      }
    });

    // Depois da assinatura: o cliente inicial carrega contatos e o aviso como uma escolha do usuário.
    if (data.idPessoaClienteInicial) this.clienteControl.setValue(data.idPessoaClienteInicial);
  }

  /** Só vale o vendedor que está nas opções do campo (ativo e dentro do escopo). */
  private vendedorDisponivel(idVendedor: number | null | undefined): number | null {
    return idVendedor != null && this.opcoesVendedor.some(v => v.idVendedor === idVendedor) ? idVendedor : null;
  }

  /**
   * RC-04: para quem não é vendedor, escolher ou trocar o cliente refaz o vendedor responsável a
   * partir do cadastro do cliente. O vendedor logado continua como responsável.
   */
  onClienteSelecionado(selecionado: LookupItemStruct | number | null): void {
    if (this.data.escopo.isVendedor) return;

    const cliente = typeof selecionado === 'object' ? selecionado : null;
    if (!cliente) {
      this.hintVendedor = '';
      return;
    }

    const idVendedor = this.vendedorDisponivel(cliente.idVendedor);
    this.vendedorControl.setValue(idVendedor);
    this.hintVendedor = idVendedor == null
      ? 'Este cliente não tem vendedor ativo. Escolha o vendedor responsável.'
      : '';
  }

  /** Escolher o vendedor à mão tira o aviso de cliente sem vendedor. */
  onVendedorSelecionado(): void {
    this.hintVendedor = '';
  }

  /** Sem Visualizar do CRM o endpoint recusaria; o aviso simplesmente não aparece. */
  private verificarOportunidadeEmAndamento(idPessoa: number): void {
    const listaPermissoes = this.authService.getUserInfos()?.listaPermissoes;
    if (!listaPermissoes?.includes(PermissaoEnum.Vendas_Crm_Visualizar)) return;

    this.oportunidadeService.getUltimasDoCliente(idPessoa, 1).subscribe({
      next: response => {
        // Resposta de um cliente que já foi trocado não vale para o atual.
        if (this.clienteControl.value !== idPessoa) return;
        this.clienteComOportunidadeEmAndamento = !response.isError && response.possuiEmAndamento;
      },
      // Aviso acessório: falhar aqui não impede criar a oportunidade.
      error: () => { },
    });
  }

  private carregarContatos(idPessoa: number): void {
    this.carregandoContatos = true;

    this.pessoaLookupService.listContatoPessoaSelect(idPessoa).subscribe({
      next: response => {
        this.carregandoContatos = false;
        this.contatos = response.isError ? [] : response.list ?? [];
      },
      // Contato é opcional: falhar aqui não impede criar a oportunidade.
      error: () => {
        this.carregandoContatos = false;
        this.contatos = [];
      },
    });
  }

  /** "+ Novo contato" só aparece com cliente escolhido e permissão de alterar o CRM e o cadastro de pessoas. */
  get podeCriarContato(): boolean {
    return this.clienteControl.value != null && !this.motivoContatoSemPermissao;
  }

  abrirContatoNovo(): void {
    // A opção "+ Novo contato" entra no controle como valor 0: a oportunidade fica sem contato da lista.
    this.contatoControl.setValue(null);
    if (!this.podeCriarContato || this.carregandoContatos) return;
    this.contatoNovoForm.reset();
    this.contatoNovoAberto = true;
  }

  cancelarContatoNovo(): void {
    if (this.salvando) return;
    this.contatoNovoForm.reset();
    this.contatoNovoAberto = false;
  }

  get semContatos(): boolean {
    return this.clienteControl.value != null && !this.carregandoContatos && !this.contatos.length;
  }

  get valido(): boolean {
    return !this.motivoBloqueio;
  }

  /** Tooltip do "Criar oportunidade" desabilitado: o primeiro campo obrigatório que falta. */
  get motivoBloqueio(): string {
    if (!this.titulo.trim()) return 'Informe o assunto.';
    if (this.clienteControl.value == null) return 'Informe o cliente.';
    if (this.idOrigemLead == null) return 'Informe a origem do lead.';
    if (this.vendedorControl.value == null) return 'Informe o vendedor responsável.';
    // Criar agora levaria a oportunidade sem o contato que está sendo preenchido.
    if (this.contatoNovoAberto && this.contatoNovoForm.invalid) return 'Informe nome, telefone e e-mail do novo contato.';
    return '';
  }

  corDaPrioridade(chave: PrioridadeChave): string {
    return this.prioridades.find(p => p.chave === chave)?.cor ?? '#9AA0A6';
  }

  rotuloDaPrioridade(chave: PrioridadeChave): string {
    return this.prioridades.find(p => p.chave === chave)?.label ?? '';
  }

  fechar(): void {
    if (this.salvando) return;
    this.dialogRef.close(null);
  }

  criar(): void {
    if (!this.valido || this.salvando) return;
    this.salvando = true;

    if (!this.contatoNovoAberto) {
      this.criarOportunidade(this.contatoControl.value || null);
      return;
    }

    // Contato novo: grava no cadastro do cliente e cria a oportunidade já com ele.
    const idPessoa = this.clienteControl.value!;
    const { nome, telefone, email } = this.contatoNovoForm.getRawValue();
    this.pessoaService.criarContato(idPessoa, { nome: nome.trim(), telefone, email: email.trim() }).subscribe({
      next: response => {
        if (response.isError || !response.contato) {
          this.salvando = false;
          this.alertService.show(
            'Aviso', response.errorDescription || 'Não foi possível criar o contato.', AlertType.warning);
          return;
        }

        // O contato já está no cadastro: se a oportunidade falhar, a nova tentativa usa ele em vez de criar outro.
        this.contatos = [...this.contatos, response.contato];
        this.contatoControl.setValue(response.contato.id);
        this.contatoNovoAberto = false;
        this.criarOportunidade(response.contato.id);
      },
      error: () => {
        this.salvando = false;
        this.alertService.show(
          'Aviso', 'Não foi possível criar o contato agora. Tente novamente em alguns instantes.', AlertType.warning);
      },
    });
  }

  private criarOportunidade(idPessoaContato: number | null): void {
    const request: CriarOportunidadeRequest = {
      titulo: this.titulo.trim(),
      idEtapaKanban: this.idEtapaKanban,
      idPessoaCliente: this.clienteControl.value!,
      idPessoaContato,
      idVendedorResponsavel: this.vendedorControl.value!,
      idOrigemLead: this.idOrigemLead!,
      idCanalContato: this.idCanalContato,
      idTipoPrioridade: ID_POR_PRIORIDADE[this.prioridade],
      valorEstimado: this.valorEstimado,
      idsTagCrm: this.tagsControl.value.length ? this.tagsControl.value : undefined,
    };

    this.oportunidadeService.create(request).subscribe({
      next: response => {
        this.salvando = false;

        if (response.isError || !response.oportunidade) {
          this.alertService.show(
            'Atenção',
            response.errorDescription || 'Não foi possível criar a oportunidade.',
            AlertType.warning
          );
          return;
        }

        this.alertService.show('Sucesso', 'Oportunidade criada.', AlertType.success);
        this.dialogRef.close({ oportunidade: response.oportunidade } as NovaOportunidadeResult);
      },
      error: () => {
        this.salvando = false;
        this.alertService.show('Erro', 'Não foi possível criar a oportunidade.', AlertType.error);
      },
    });
  }
}
